import { z } from 'zod';
import { blankToNull } from './blankToNull';

// The tracking read, as DELIVERED on `GET /app_service/panel/v1/deliveries?tracking=<term>`
// (AS-054, CBX-11350) and verified against dev on 2026-09-09 by reading a real payload.
//
// This file used to be the only one in this directory with no Zod in it, because the endpoint was a
// REQUEST and a schema would have frozen the panel's own conjecture into a runtime gate. Backend
// delivered the shape it was asked for — six keys per row, the `{count, results}` envelope, no
// cursor — so the mirror is now a mirror and the type NAMES are kept exactly as the hand-written
// domain types had them, which is what keeps every consumer where it was.
//
// A person a delivery can be handed to. The same triple the building-wide list (AS-039) and the
// delivery detail (AS-052) already deliver, with the same nesting and the same two absences — the
// request reused a known shape on purpose instead of inventing one, so the mirror reuses its rules:
//
// - `nullish` on the OBJECT: `recipient: null` means there is no titular at all — the case the
//   loose «Entrega» tab exists for — and an absent key means the same thing, because a deploy that
//   lags its code omits it outright (this is exactly how the building-wide list shipped).
// - `nullish` AGAIN on `name` and `phone`, which is not redundant: a PRESENT object with those at
//   null is a DIFFERENT fact — the titular exists and its row could not be read (a dangling id, an
//   «Usuario» given up) — and the `id` is the proof. The modal paints «—» for both; the domain
//   object keeps them apart, and the tab rule needs it, since a titular with no phone has no tab to
//   key by.
// - `id` is a plain number: if the object travels, the id is there. No consumer reads it yet, so it
//   stops here rather than reaching a screen.
export const TrackingRecipientSchema = z
  .object({
    id: z.number(),
    name: z.string().nullish(),
    phone: z.string().nullish(),
  })
  .strict();

// `.strict()` on the row, and it is the load-bearing decision of this schema rather than tidiness.
// The read was requested MINIMIZED — six fields, and deliberately no state, box, carrier, email or
// extra dates, because the detail already serves those once a delivery is open. A permissive row
// would leave that minimization a promise nobody verifies, so a field arriving that nobody agreed
// to fails the parse. Failing here costs the floating «no se pudo completar la búsqueda» notice,
// never a blank screen: the read is rejected, which the hook already words apart from «no matches».
// That is the cheap outcome, and it is the only barrier against this read quietly starting to carry
// more of a third party's PII than it was asked for.
export const TrackingMatchSchema = z
  .object({
    id: z.string().min(1),
    // Present by construction: these rows were selected BY tracking, so a match without one would
    // be a contradiction rather than a missing value.
    tracking: z.string().min(1),
    // Nullable for the same reason its sibling row is: a `failed`/`cancelled` delivery has a
    // tracking and never reached a box, so it arrives with no stored date. It normalizes to '' —
    // what this domain already means by «absent» — because the domain type declares a string, and
    // the formatter renders an unparseable instant as itself, so the cell reads as an em dash
    // instead of breaking the row.
    stored_date: z.string().nullable(),
    // The instant above is rendered in THIS zone and never the agent's own: a CC agent in Madrid
    // working a UK case has to read the locker's local time (ADR 0011). A plain string, as its
    // three sibling reads declare it — the formatter is total against a zone `Intl` cannot resolve,
    // so a garbage zone degrades to the raw instant rather than throwing a render away.
    locker_timezone: z.string(),
    // `nullish`, not a required string: the shared composer yields null for a building with no
    // address row, one composing to an empty string, an unreadable building, and a delivery that
    // never reached one. Required here means `.strict()` throws on such a row and the whole match
    // list is lost, which is a worse answer than one row reading «—». The object stays `.strict()`
    // — relaxing a member must not relax what may sit beside it.
    location: z.object({ address: z.string().nullish() }).strict(),
    recipient: TrackingRecipientSchema.nullish(),
  })
  .strict()
  .transform((row) => ({
    id: row.id,
    tracking: row.tracking,
    storedDate: row.stored_date ?? '',
    lockerTimezone: row.locker_timezone,
    // One absence rule for this field across the three reads that carry it, so a consumer never
    // has to ask which spelling of «no address» this one uses.
    location: { address: blankToNull(row.location.address) },
    // Field by field, never a spread: building the object explicitly is what keeps a field backend
    // adds later from reaching a screen unnoticed — the same reason the row is strict.
    recipient: row.recipient
      ? {
          id: row.recipient.id,
          name: blankToNull(row.recipient.name),
          phone: blankToNull(row.recipient.phone),
        }
      : null,
  }));

// The envelope travels WHOLE, unlike the sibling reads that unwrap to bare rows: `count` is the
// total of matches in the window while the rows are capped at 50, so the two numbers are different
// facts and a caller needs both to say «this list is truncated» instead of lying by omission.
//
// `.strict()`, and NOT the `next: z.null().optional()` the carriers read uses. The difference is
// what `count` is spent on. There, count-vs-length is free to act as the truncation cross-check;
// here the panel SPENDS that gap telling the agent the list is capped, so a page of 20 out of 100
// would arrive wearing the clothes of a capped answer and the modal would report a total it is not
// showing. With no signal left over, a paginated answer has to fail LOUD — and strict catches every
// spelling of it (`next`, `cursor`, `has_more`), not just the one guessed today.
export const TrackingSearchResponseSchema = z
  .object({
    count: z.number(),
    results: z.array(TrackingMatchSchema),
  })
  .strict();

// The triple itself, absence excluded: `TrackingMatch['recipient']` is what carries the null, and a
// type named after the person should not.
export type TrackingRecipient = NonNullable<z.infer<typeof TrackingMatchSchema>['recipient']>;
export type TrackingMatch = z.infer<typeof TrackingMatchSchema>;
export type TrackingSearchResponse = z.infer<typeof TrackingSearchResponseSchema>;
